import { abc2svg } from '@ezchops/abc2svg';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useElementStyle, useSize } from '../../hooks';
export default function AbcSheet(props) {
    const { voice, signature, unitNoteLength, sheetStyle, debug } = props;
    const containerRef = useRef(null);
    const size = useSize(containerRef);
    const style = useElementStyle(containerRef);
    const [barRects, setBarRects] = useState([]);
    const [divElement, setDivElement] = useState();
    const handleSvg = useCallback((element) => {
        const svg = element?.querySelector('svg');
        if (svg != null && props.position != null) {
            const divRect = element.getBoundingClientRect();
            const barRects = element.querySelectorAll('rect.bar-rect');
            const barRectsArray = Array.from(barRects).map((rect) => {
                const rectRect = rect.getBoundingClientRect();
                return ({
                    bar: parseInt(rect.getAttribute('data-bar') ?? '-1'),
                    x: rectRect.x - divRect.x, width: rectRect.width
                });
            });
            setBarRects(barRectsArray);
            setDivElement(element);
        }
    }, [props.position]);
    useEffect(() => {
        if (divElement != null && barRects.length > 0 && props.position != null) {
            const currentBar = props.position.measure;
            const divRect = divElement.getBoundingClientRect();
            divElement.querySelector('rect.bar-rect.active')?.classList?.remove('active');
            divElement.querySelector(`rect.bar-rect[data-bar="${currentBar}"]`)?.classList?.add('active');
            divElement.classList.add('has-active');
            const currentBarRect = barRects[currentBar];
            const nextBarRect = barRects[currentBar + 1];
            const translateX = getTranslateX(divElement);
            if (nextBarRect != null && -translateX + divRect.width < nextBarRect.x + nextBarRect.width) {
                divElement.style.transform = `translate(-${currentBarRect.x}px, 0)`;
            }
            else if (currentBarRect != null && -translateX > currentBarRect.x) {
                divElement.style.transform = `translate(-${currentBarRect.x}px, 0)`;
            }
        }
        else if (divElement != null) {
            divElement.classList?.remove('has-active');
        }
        return (() => {
            if (divElement != null) {
                divElement.style.transform = '';
                divElement.classList?.remove('has-active');
            }
        });
    }, [barRects, barRects.length, divElement, props.position]);
    const [svg, errors] = useMemo(() => {
        if (size == null || style == null) {
            return [undefined, undefined];
        }
        const abcStyle = {
            musicfont: 'abc2svg 24',
            ...sheetStyle,
            width: Math.floor(size.width),
            color: style.getPropertyValue('color'),
            background: style.getPropertyValue('background-color')
        };
        return abc2svg(voice, signature, unitNoteLength, abcStyle);
    }, [size, style, sheetStyle, voice, signature, unitNoteLength]);
    return React.createElement(SheetContainer, { className: "AbcSheet", ref: containerRef }, svg == null
        ? (errors == null || debug != true)
            ? React.createElement(Skeleton, { variant: "rectangular", animation: "wave" })
            : React.createElement(Stack, { sx: { fontFamily: 'monospace' }, direction: "column", spacing: 2 },
                React.createElement(Stack, { direction: "column" }, errors.map((error, i) => React.createElement(Box, { key: i }, JSON.stringify(error)))),
                typeof voice === 'object'
                    ? React.createElement(Stack, { direction: "column" }, Object.entries(voice).map(([i, v]) => React.createElement(Stack, { key: i, direction: "row", spacing: 2 },
                        React.createElement(Box, null, i),
                        React.createElement(Box, null, v))))
                    : React.createElement(Box, null, JSON.stringify(voice)))
        : React.createElement("div", { ref: handleSvg, className: "AbcSheet-sheet", dangerouslySetInnerHTML: { __html: svg ?? '' } }));
}
export function InlineAbcSheet(props) {
    const svgRef = useRef(null);
    const style = useElementStyle(svgRef);
    const { voice, signature, unitNoteLength, sheetStyle } = props;
    const [svg, errors] = useMemo(() => {
        const abcStyle = {
            musicfont: 'abc2svg 24',
            ...sheetStyle,
            color: style?.getPropertyValue('color'),
            background: style?.getPropertyValue('background-color')
        };
        return abc2svg(voice, signature, unitNoteLength, abcStyle);
    }, [style, sheetStyle, voice, signature, unitNoteLength]);
    return React.createElement("div", { ref: svgRef, dangerouslySetInnerHTML: { __html: svg ?? '' } });
}
function getTranslateX(element) {
    const style = window.getComputedStyle(element);
    const matrix = new WebKitCSSMatrix(style.transform);
    return matrix.m41;
}
const SheetContainer = styled('div')({
    width: '100%',
    height: '100%',
    minHeight: '60px',
    '@media print': {
        maxWidth: '16cm !important',
    },
    '& .AbcSheet-sheet': {
        '&.has-active': {
            '& rect.bar-rect': {
                '&:not(.active)': {
                    fill: 'rgba(0, 0, 0, 0.5)'
                }
            }
        },
        '& svg': {
            display: 'block',
        }
    }
});
//# sourceMappingURL=AbcSheet.js.map